import {FC, useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {Door} from '@shared/models/api_models';

import {UnthemedButton} from '@shared_web/components/core/button';
import {Input} from '@shared_web/components/core/input';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {SvgIcon} from '@shared_web/components/core/svg_icon';

import {Translate, translate} from '@mavismart_frontend/components/translate';
import {icon_check} from '@mavismart_frontend/icons/icon_check';
import {icon_cross} from '@mavismart_frontend/icons/icon_cross';
import {icon_pencil2} from '@mavismart_frontend/icons/icon_pencil2';
import {apiCall} from '@mavismart_frontend/lib/api';
import {MAIN_COLOR, TAB_TITLE_HEIGHT, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {updateDoorLabel} from '@mavismart_frontend/stores/all_doors_store';

interface DoorTitleProps {
  door: Door;
}

export const DoorLabel: FC<DoorTitleProps> = ({door}) => {
  const account = useAccount();

  const [label, setLabel] = useState<string | undefined>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const previousLabel = door?.label;

  useEffect(() => setLabel(door.label), [door]);

  const handlePencilClick = useCallback(() => {
    setIsEditing(!isEditing);
  }, [isEditing]);

  const handleCancelLabel = useCallback(() => {
    setIsEditing(false);
    setLabel(previousLabel ?? '');
  }, [previousLabel]);

  const handleLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  }, []);

  const handleSaveLabel = useCallback(async () => {
    if (!door || label === undefined || label === previousLabel) {
      return;
    }
    try {
      await apiCall('POST /update-door-label', {doorId: door.id, newLabel: label, previousLabel});
      showSuccess(translate('door_label_updated', getUserLanguage(account)));
    } catch {
      showAlert(translate('error_updating_door_label', getUserLanguage(account)));
      setLabel(previousLabel ?? door.id);
    } finally {
      setIsEditing(false);
      updateDoorLabel(door.id, label);
    }
  }, [label, door, previousLabel, account]);

  return (
    <Wrapper>
      <TitleWrapper>
        {isEditing ? (
          <>
            <Input
              value={label}
              onChange={handleLabelChange}
              themeOverrides={{input: {height: TAB_TITLE_HEIGHT, focusOutlineWidth: 0}}}
            />
            <SvgIcon icon={icon_check} size={25} onClick={handleSaveLabel} color="transparent" />
            <SvgIcon icon={icon_cross} size={25} onClick={handleCancelLabel} color={MAIN_COLOR} />
          </>
        ) : (
          <UnthemedButtonStyled onClick={handlePencilClick}>
            {label ? (
              <>
                {/* <Translate value="label" /> {label} */}
                {label}
              </>
            ) : (
              <Translate value="add_a_label" />
            )}
            <SvgIcon icon={icon_pencil2} size={20} color="transparent" />
          </UnthemedButtonStyled>
        )}
      </TitleWrapper>
    </Wrapper>
  );
};

DoorLabel.displayName = 'DoorTitle';

const Wrapper = styled.div`
  display: flex;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UnthemedButtonStyled = styled(UnthemedButton)`
  background: none;
  gap: 10px;
  color: ${TEXT_MAIN_COLOR};
  font-size: 22px;
  height: 40px;
`;
